import styled from '@emotion/styled';

import { WIDGET_Z_INDEX } from '@hotjar/webapp/features/Heatmaps/ContinuousCaptureReport/ReportWidgets/constants';
import { ExistingHighlightPopover } from '@hotjar/webapp/features/Highlights/ExistingHighlightPopover';
import { useCurrentSite } from '@hotjar/site';
import { useCurrentUser } from '@hotjar/user';
import { useHighlight } from '@hotjar/webapp/features/Highlights/SingleHighlight/use-highlight';
import {
  POPOVER_WIDTH,
  POPOVER_MAX_HEIGHT,
} from '@hotjar/webapp/features/Highlights/HighlightPopoverContainer';
import { CommentsContext } from '@hotjar/webapp/contexts/comments';

export const SELECTED_AREA_TO_POPOVER_GAP = 4;

const PopoverBox = styled.div<{
  position: { top: number | null; left: number; bottom: number | null };
}>`
  position: absolute;
  ${({ position }) => (position.bottom ? `bottom: ${position.bottom}px` : null)};
  ${({ position }) => (position.top ? `top: ${position.top}px` : null)};

  left: ${(props) => props.position.left}px;
  z-index: ${WIDGET_Z_INDEX - 8}; // one index less than the Avatar to be on top when displayed
`;

interface AvatarPopoverProps {
  highlightId: string;
  heatmapWidth: number;
  heatmapHeight: number;
  heatmapScale: number;
  isDesktop: boolean;
  deactivateCurrentItem: () => void;
}

export const AvatarPopover = ({
  highlightId,
  heatmapWidth,
  heatmapHeight,
  heatmapScale,
  isDesktop,
  deactivateCurrentItem,
}: AvatarPopoverProps): JSX.Element | null => {
  const { data: currentSite } = useCurrentSite();
  const { data: currentUser } = useCurrentUser();
  const { data: highlight } = useHighlight(String(currentSite?.id), highlightId);

  if (!highlight || highlight.type !== 'heatmap') {
    return null;
  }

  const getPosition = (): { top: number | null; left: number; bottom: number | null } => {
    let top: number | null = highlight.metadata.selectedArea.y2 * heatmapScale;
    let left = highlight.metadata.selectedArea.x1 * heatmapScale;
    let bottom: number | null = null;

    const areaWidth =
      (highlight.metadata.selectedArea.x2 - highlight.metadata.selectedArea.x1) * heatmapScale;

    // Center popover in relation to selected area
    const offsetX = (areaWidth - POPOVER_WIDTH) / 2;
    left += offsetX;

    // If the popover is going to flow off the left of the heatmap we need to move it.
    if (left < 0) {
      left = 0;
    }

    // If the popover will now flow off the right edge (due to a selection that is shorter than popover)
    if (left + POPOVER_WIDTH > heatmapWidth * heatmapScale) {
      left = heatmapWidth * heatmapScale - POPOVER_WIDTH;
    }

    // If the popup will flow off the bottom of the heatmap we need to nudge it up into the screen.
    if (top + SELECTED_AREA_TO_POPOVER_GAP + POPOVER_MAX_HEIGHT > heatmapHeight * heatmapScale) {
      top = null;
      bottom =
        (heatmapHeight - highlight.metadata.selectedArea.y1) * heatmapScale +
        2 * SELECTED_AREA_TO_POPOVER_GAP;

      // If the popup will now flow off the top of the heatmap we need to nudge it down into the screen.
      bottom =
        bottom > heatmapHeight * heatmapScale - POPOVER_MAX_HEIGHT + SELECTED_AREA_TO_POPOVER_GAP
          ? heatmapHeight * heatmapScale - POPOVER_MAX_HEIGHT
          : bottom;
    } else {
      top += SELECTED_AREA_TO_POPOVER_GAP;
    }

    return {
      top,
      left,
      bottom,
    };
  };

  if (!currentUser || !currentSite || !highlight || highlight.type !== 'heatmap') {
    return null;
  }

  return (
    <CommentsContext>
      <PopoverBox role="dialog" position={getPosition()}>
        <ExistingHighlightPopover
          currentUser={currentUser}
          deactivateCurrentItem={deactivateCurrentItem}
          isDesktop={isDesktop}
          item={{
            ...highlight,
            id: highlight.id,
          }}
          organizationId={currentSite?.organizationId}
          siteId={String(currentSite?.id)}
        />
      </PopoverBox>
    </CommentsContext>
  );
};
